import { Link } from "react-router-dom";
import type { Post } from "@/mocks/posts";

function formatHeat(heat: number) {
  return heat >= 10000 ? `${(heat / 10000).toFixed(1)}万` : `${heat}`;
}

type PostCardProps = {
  post: Post;
  variant?: "grid" | "compact";
};

export default function PostCard({ post, variant = "grid" }: PostCardProps) {
  if (variant === "compact") {
    return (
      <article className="group">
        <Link to={`/post/${post.id}`} className="flex gap-4 cursor-pointer">
          <div className="w-28 h-20 shrink-0 rounded-md overflow-hidden bg-background-200">
            <img
              src={post.image}
              alt={post.imageAlt}
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
          </div>
          <div className="min-w-0 flex flex-col justify-between py-0.5">
            <h4 className="text-sm font-medium text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
              {post.title}
            </h4>
            <div className="flex items-center gap-3 text-xs text-foreground-500 mt-1">
              <span className="whitespace-nowrap">{post.dateLabel}</span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-fire-line" aria-hidden="true"></i>
                {formatHeat(post.heat)}
              </span>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  return (
    <article className="group h-full flex flex-col bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-colors">
      <Link to={`/post/${post.id}`} className="cursor-pointer flex flex-col h-full">
        <div className="w-full h-44 overflow-hidden bg-background-200">
          <img
            src={post.image}
            alt={post.imageAlt}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="p-4 md:p-5 flex flex-col flex-1">
          <div className="flex items-center gap-2 mb-2.5">
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 font-medium whitespace-nowrap">
              {post.category}
            </span>
            <span className="text-[11px] text-foreground-500 whitespace-nowrap">{post.dateLabel}</span>
          </div>
          <h3 className="font-heading font-bold text-base text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
            {post.title}
          </h3>
          <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2 flex-1">
            {post.excerpt}
          </p>
          <div className="mt-4 pt-3 border-t border-background-200 flex items-center justify-between text-xs text-foreground-500">
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-user-3-line" aria-hidden="true"></i>
              {post.author}
            </span>
            <span className="flex items-center gap-3">
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-fire-line text-accent-500" aria-hidden="true"></i>
                {formatHeat(post.heat)}
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-chat-3-line" aria-hidden="true"></i>
                {post.comments}
              </span>
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
}